<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
// Bloco 4, "Quando e em que ritmo". Visível: quando, ritmo e a frase que explica
// o ritmo com a estimativa calculada. Todo o resto do formulário antigo (ações
// depois de enviar, limite diário, dias, atribuição, palavra-chave de SMS,
// rodapé de descadastro, destino no Kanban) continua alcançável no opcional.
import { computed, ref } from 'vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import { PACE_ORDER, paceConfig } from './campaignFormHelpers';

const props = defineProps({
  state: { type: Object, required: true },
  channelMode: { type: String, default: 'official' },
  isFreeText: { type: Boolean, default: false },
  recipientCount: { type: Number, default: 0 },
  duration: { type: Object, default: () => ({ minutes: 0, days: 0 }) },
  agentOptions: { type: Array, default: () => [] },
  teamOptions: { type: Array, default: () => [] },
  aiAssistantOptions: { type: Array, default: () => [] },
  funnelOptions: { type: Array, default: () => [] },
  kanbanDestStageOptions: { type: Array, default: () => [] },
  whatsappInboxOptions: { type: Array, default: () => [] },
  kaTemplateOptions: { type: Array, default: () => [] },
  kaSelectedTemplate: { type: Object, default: null },
  kaTargetIsFreeText: { type: Boolean, default: false },
  minDateTime: { type: String, default: '' },
});

const PACE_LABELS = { fast: 'Rápido', normal: 'Normal', safe: 'Seguro' };

const WEEKDAYS = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

const ASSIGNMENT_MODES = [
  { value: 'agent', label: 'Atribuir a um vendedor' },
  { value: 'ai', label: 'Continuar com agente de IA' },
];

const ASSIGNMENT_STRATEGIES = [
  { value: 'none', label: 'Sem atribuição (cai no pool)' },
  { value: 'fixed', label: 'Vendedor fixo' },
  { value: 'round_robin', label: 'Rodízio na equipe' },
  { value: 'region_ddd', label: 'Por região (DDD)' },
  { value: 'team', label: 'Equipe' },
];

const config = computed(() => paceConfig(props.state.pace));

// A frase que explica o ritmo, com a estimativa calculada de verdade a partir da
// contagem real do público.
const paceSentence = computed(() => {
  const { minIntervalS, maxIntervalS, windowStart, windowEnd } = config.value;
  const base = `${PACE_LABELS[props.state.pace]}: uma mensagem a cada ${minIntervalS} a ${maxIntervalS} segundos, entre ${windowStart}h e ${windowEnd}h.`;
  if (!props.recipientCount) return base;
  const { minutes, days } = props.duration;
  if (days > 1) {
    return `${base} ${props.recipientCount} pessoas levam cerca de ${days} dias.`;
  }
  if (minutes >= 60) {
    const hours = Math.round((minutes / 60) * 10) / 10;
    return `${base} ${props.recipientCount} pessoas levam cerca de ${hours} horas.`;
  }
  return `${base} ${props.recipientCount} pessoas levam cerca de ${minutes} minutos.`;
});

const toggleDay = value => {
  const idx = props.state.daysOfWeek.indexOf(value);
  if (idx === -1) props.state.daysOfWeek.push(value);
  else props.state.daysOfWeek.splice(idx, 1);
};

const addDddRow = () => props.state.dddMap.push({ ddd: '', agentId: null });
const removeDddRow = idx => props.state.dddMap.splice(idx, 1);

const kaTemplateParserRef = ref(null);
defineExpose({ kaTemplateParserRef });
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="grid gap-3 sm:grid-cols-2">
      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">Quando</span>
        <div class="flex gap-1.5">
          <button
            v-for="mode in ['now', 'later']"
            :key="mode"
            type="button"
            class="px-2.5 py-1 text-xs border rounded-full"
            :class="
              state.scheduleMode === mode
                ? 'border-n-blue-8 bg-n-alpha-black2 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-black2'
            "
            :aria-pressed="state.scheduleMode === mode"
            @click="state.scheduleMode = mode"
          >
            {{ mode === 'now' ? 'Agora' : 'Agendar' }}
          </button>
        </div>
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">Ritmo</span>
        <div class="flex gap-1.5">
          <button
            v-for="pace in PACE_ORDER"
            :key="pace"
            type="button"
            class="px-2.5 py-1 text-xs border rounded-full"
            :class="
              state.pace === pace
                ? 'border-n-blue-8 bg-n-alpha-black2 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-black2'
            "
            :aria-pressed="state.pace === pace"
            @click="state.pace = pace"
          >
            {{ PACE_LABELS[pace] }}
          </button>
        </div>
      </div>
    </div>

    <Input
      v-if="state.scheduleMode === 'later'"
      v-model="state.scheduledAt"
      type="datetime-local"
      label="Começa em"
      :min="minDateTime"
    />

    <p class="text-xs text-n-slate-11" data-testid="pace-sentence">
      {{ paceSentence }}
    </p>

    <Accordion title="Opcional" :is-open="false">
      <div class="flex flex-col gap-4 pt-2">
        <!-- Depois de enviar -->
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-n-slate-12">Depois de enviar</span>
          <label class="flex items-center gap-2 text-xs text-n-slate-11">
            <input v-model="state.requireHumanResponse" type="checkbox" class="rounded" />
            Resposta do cliente abre na fila de atendimento
          </label>
          <ComboBox
            v-model="state.assignmentMode"
            :options="ASSIGNMENT_MODES"
            class="[&>div>button]:bg-n-alpha-black2"
          />
          <template v-if="state.assignmentMode === 'ai'">
            <ComboBox
              v-model="state.aiAssistantId"
              :options="aiAssistantOptions"
              placeholder="Agente de IA que continua a conversa"
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </template>
          <template v-else>
            <ComboBox
              v-model="state.assignmentStrategy"
              :options="ASSIGNMENT_STRATEGIES"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <ComboBox
              v-if="state.assignmentStrategy === 'fixed'"
              v-model="state.assignmentAgentId"
              :options="agentOptions"
              placeholder="Vendedor"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <TagMultiSelectComboBox
              v-else-if="state.assignmentStrategy === 'round_robin'"
              v-model="state.assignmentAgentIds"
              :options="agentOptions"
              placeholder="Vendedores do rodízio"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <ComboBox
              v-else-if="state.assignmentStrategy === 'team'"
              v-model="state.assignmentTeamId"
              :options="teamOptions"
              placeholder="Equipe"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <div v-else-if="state.assignmentStrategy === 'region_ddd'" class="flex flex-col gap-1">
              <div
                v-for="(row, idx) in state.dddMap"
                :key="idx"
                class="flex items-center gap-2"
              >
                <Input v-model="row.ddd" placeholder="DDD" class="w-20" />
                <ComboBox
                  v-model="row.agentId"
                  :options="agentOptions"
                  placeholder="Vendedor"
                  class="flex-1 [&>div>button]:bg-n-alpha-black2"
                />
                <button type="button" class="text-xs text-n-slate-10" @click="removeDddRow(idx)">
                  remover
                </button>
              </div>
              <button
                type="button"
                class="self-start text-xs text-n-blue-11 hover:underline"
                @click="addDddRow"
              >
                + faixa de DDD
              </button>
              <ComboBox
                v-model="state.regionFallbackAgentId"
                :options="agentOptions"
                placeholder="Vendedor para DDD não listado"
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
          </template>
          <div v-if="state.requireHumanResponse" class="grid gap-2 sm:grid-cols-2">
            <ComboBox
              v-model="state.kanbanDestFunnelId"
              :options="funnelOptions"
              placeholder="Destino no Kanban: funil"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <ComboBox
              v-model="state.kanbanDestStageId"
              :options="kanbanDestStageOptions"
              placeholder="Etapa"
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>
        </div>

        <!-- Limites -->
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-n-slate-12">Limites</span>
          <div class="grid gap-2 sm:grid-cols-3">
            <Input
              v-model="state.dailyLimit"
              type="number"
              label="Limite por dia"
              :placeholder="String(config.dailyLimit)"
            />
            <Input
              v-model="state.windowStart"
              type="number"
              label="Começa às"
              :placeholder="String(config.windowStart)"
            />
            <Input
              v-model="state.windowEnd"
              type="number"
              label="Para às"
              :placeholder="String(config.windowEnd)"
            />
          </div>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="day in WEEKDAYS"
              :key="day.value"
              type="button"
              class="px-2 py-0.5 text-xs border rounded-full"
              :class="
                state.daysOfWeek.includes(day.value)
                  ? 'border-n-blue-8 bg-n-alpha-black2 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="toggleDay(day.value)"
            >
              {{ day.label }}
            </button>
          </div>
          <p class="text-xs text-n-slate-11">
            Sem nenhum dia marcado, a campanha envia todos os dias.
          </p>
        </div>

        <!-- Texto -->
        <div v-if="isFreeText" class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-n-slate-12">Texto</span>
          <label class="flex items-center gap-2 text-xs text-n-slate-11">
            <input v-model="state.optOutFooter" type="checkbox" class="rounded" />
            Incluir o rodapé de descadastro ("responda SAIR")
          </label>
          <Input
            v-model="state.nameFallback"
            label="Quando não sabemos o nome, escrever"
            placeholder="Em branco: a saudação sai sem nome"
          />
        </div>

        <!-- Palavra-chave (SMS) -->
        <div v-if="channelMode === 'sms'" class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-n-slate-12">
            Palavra-chave de SMS
          </span>
          <label class="flex items-center gap-2 text-xs text-n-slate-11">
            <input v-model="state.kaEnabled" type="checkbox" class="rounded" />
            Quando a pessoa responder o SMS, continuar no WhatsApp
          </label>
          <template v-if="state.kaEnabled">
            <ComboBox
              v-model="state.kaTargetInboxId"
              :options="whatsappInboxOptions"
              placeholder="Caixa de WhatsApp que continua a conversa"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <Input
              v-model="state.kaCustomKeywords"
              label="Palavras extras"
              placeholder="SIM, QUERO, ORCAMENTO"
            />
            <textarea
              v-if="kaTargetIsFreeText"
              v-model="state.kaMessage"
              rows="3"
              class="w-full px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
              placeholder="Mensagem que abre a conversa no WhatsApp"
            />
            <template v-else>
              <ComboBox
                v-model="state.kaTemplateId"
                :options="kaTemplateOptions"
                placeholder="Modelo aprovado da continuação"
                class="[&>div>button]:bg-n-alpha-black2"
              />
              <WhatsAppTemplateParser
                v-if="kaSelectedTemplate"
                ref="kaTemplateParserRef"
                :template="kaSelectedTemplate"
                hide-media-input
              />
            </template>
            <label class="flex items-center gap-2 text-xs text-n-slate-11">
              <input v-model="state.kaResetReassign" type="checkbox" class="rounded" />
              Fechar a conversa antiga e abrir outra com um atendente novo
            </label>
          </template>
        </div>
      </div>
    </Accordion>
  </div>
</template>
